import React, { useState } from 'react';
import { getMainAxios } from '../utils/util';

/**
 * 1분봉 추세추종 전략 백테스트 화면.
 *
 * 신호 계산은 서버가 **운영과 같은 전략 클래스**(LeanQuant.Algorithms)로 한다.
 * 화면에서 따로 구현하지 않는다 — 그러면 "화면에서 벌던 전략"과
 * "실제로 도는 전략"이 갈린다.
 *
 * 기본값은 2026-09-12 검증에서 채택된 I4 Donchian(90) 이다.
 *
 * 계좌는 **simtest(가상체결) 고정**이다. 백테스트는 과거 데이터로 돌리는 모의실험이라
 * 실전·모의 계좌의 실제 잔고와 섞이면 안 된다. NAV 는 simtest 초기자본(1,000만)을 쓴다.
 */

interface SymRow {
  prodId: string; prodNm: string; trades: number; pnl: number;
  winRate: number; avgPnl: number; mdd: number; pf: number; note?: string | null;
}
interface EqRow {
  d: string; pnl: number; cum: number;
  trades: number; wins: number; loses: number; winRate: number;
}
interface RunRow {
  runId: number; runAt: string; strategy: string; period: number;
  prodIds: string; startDate: string; endDate: string;
  trades: number; pnl: number; winRate: number; pf: number; mdd: number;
}
interface SymEqRow { d: string; pnl: number; cum: number; trades: number; wins: number; }
interface SymEquity { prodId: string; prodNm: string; rows: SymEqRow[]; }
interface BtRes {
  runId?: number; runAt?: string;
  symbolEquity?: SymEquity[];
  strategy: string; period: number; startDate: string; endDate: string;
  nav: number; weiPct: number; lagMin: number;
  total: { trades: number; pnl: number; winRate: number; avgPnl: number; mdd: number; pf: number };
  bySymbol: SymRow[];
  equity: EqRow[];
}

const ETF_DEFAULT = '069500,114800,229200,251340';

// 백테스트 전용 계좌 — simtest(가상체결, pfid=7). 화면에서 바꿀 수 없다.
const SIM_ACCOUNT = { pfId: 7, htsId: 'simtest', nav: 10_000_000 };
const n0 = (v: number) => Math.round(v).toLocaleString();
const pnlColor = (v: number) => (v > 0 ? '#dc2626' : v < 0 ? '#2563eb' : '#6b7280');

const QuantBacktest: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const ago = new Date(Date.now() - 120 * 86400000).toISOString().slice(0, 10);

  const [prodIds, setProdIds] = useState(ETF_DEFAULT);
  const [strategy, setStrategy] = useState('I4');
  const [period, setPeriod] = useState(90);
  const [startDate, setStartDate] = useState(ago);
  const [endDate, setEndDate] = useState(today);
  const [weiPct, setWeiPct] = useState(10);
  const [lagMin, setLagMin] = useState(1);

  const [res, setRes] = useState<BtRes | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 지난 실행 이력 — 재계산 없이 과거 결과를 다시 연다.
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [openRunId, setOpenRunId] = useState<number | null>(null);

  const loadRuns = React.useCallback(() => {
    getMainAxios()
      .get<RunRow[]>('/api/v1/quant/backtest/runs', { params: { limit: 50 } })
      .then(r => setRuns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRuns([]));
  }, []);

  React.useEffect(() => { loadRuns(); }, [loadRuns]);

  /** 지난 결과 열기 — 서버에 저장된 값을 그대로 받아 그린다(재계산 없음). */
  const openRun = (runId: number) => {
    setLoading(true); setErr(null);
    getMainAxios()
      .get<BtRes>(`/api/v1/quant/backtest/runs/${runId}`)
      .then(r => {
        setRes(r.data);
        setOpenRunId(runId);
        // 조건 입력값도 그 실행에 맞춰 되돌려, 이어서 수정·재실행하기 쉽게 한다.
        setStrategy(r.data.strategy);
        if (r.data.period) setPeriod(r.data.period);
        setStartDate(r.data.startDate);
        setEndDate(r.data.endDate);
        setWeiPct(r.data.weiPct);
        setLagMin(r.data.lagMin);
        setLoading(false);
      })
      .catch(e => { setErr(e?.message ?? '조회 실패'); setLoading(false); });
  };

  const deleteRun = (runId: number) => {
    getMainAxios()
      .delete(`/api/v1/quant/backtest/runs/${runId}`)
      .then(() => {
        loadRuns();
        if (openRunId === runId) { setRes(null); setOpenRunId(null); }
      })
      .catch(() => { /* 목록만 새로고침 */ loadRuns(); });
  };

  const run = () => {
    setLoading(true); setErr(null);
    getMainAxios()
      .get<BtRes>('/api/v1/quant/backtest', {
        // 계좌는 simtest 고정 — NAV 도 그 초기자본을 쓴다.
        params: { prodIds, strategy, period, startDate, endDate, weiPct, lagMin, nav: SIM_ACCOUNT.nav },
        timeout: 120000,
      })
      .then(r => {
        setRes(r.data);
        setOpenRunId(r.data.runId ?? null);
        setLoading(false);
        loadRuns();          // 방금 실행이 목록에 뜨도록
      })
      .catch(e => { setErr(e?.response?.data?.error ?? e?.message ?? '실행 실패'); setLoading(false); });
  };

  const lbl: React.CSSProperties = { fontSize: '11px', color: '#6b7280', marginRight: '4px' };
  const inp: React.CSSProperties = {
    fontSize: '11px', padding: '3px 6px', borderRadius: '4px',
    border: '1px solid #cbd5e1', marginRight: '10px',
  };
  const th: React.CSSProperties = {
    padding: '6px 8px', borderBottom: '2px solid #3b82f6', textAlign: 'right',
    fontWeight: 600, color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: '5px 8px', borderBottom: '1px solid #f3f4f6',
    textAlign: 'right', fontSize: '12px',
  };
  const card: React.CSSProperties = {
    backgroundColor: 'white', border: '2px solid #3b82f6',
    borderRadius: '8px', padding: '16px', marginBottom: '16px',
  };

  /**
   * 누적손익 스파크라인 (외부 라이브러리 없이 SVG).
   * 전체·종목별 모두 같은 함수로 그린다.
   */
  const sparkline = (rows: { d: string; cum: number }[], W = 900, H = 160) => {
    if (!rows.length) return null;
    const P = 24;
    const ys = rows.map(e => e.cum);
    const yMin = Math.min(0, ...ys), yMax = Math.max(0, ...ys);
    const span = yMax - yMin || 1;
    const px = (i: number) => P + (rows.length <= 1 ? 0 : (i / (rows.length - 1)) * (W - 2 * P));
    const py = (v: number) => H - P - ((v - yMin) / span) * (H - 2 * P);
    const dPath = rows.map((e, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(e.cum).toFixed(1)}`).join(' ');
    const zeroY = py(0);
    const last = ys[ys.length - 1];
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <line x1={P} y1={zeroY} x2={W - P} y2={zeroY} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3 3" />
        <path d={dPath} fill="none" stroke={last >= 0 ? '#dc2626' : '#2563eb'} strokeWidth="2" />
        <text x={P} y={12} fontSize="10" fill="#6b7280">{n0(yMax)}</text>
        <text x={P} y={H - 6} fontSize="10" fill="#6b7280">{n0(yMin)}</text>
        <text x={W - P} y={12} fontSize="10" fill={pnlColor(last)} textAnchor="end">{n0(last)}</text>
      </svg>
    );
  };

  const chart = () => {
    if (!res?.equity?.length) return null;
    const W = 900, H = 160, P = 24;
    const xs = res.equity.map((_, i) => i);
    const ys = res.equity.map(e => e.cum);
    const yMin = Math.min(0, ...ys), yMax = Math.max(0, ...ys);
    const span = yMax - yMin || 1;
    const px = (i: number) => P + (xs.length <= 1 ? 0 : (i / (xs.length - 1)) * (W - 2 * P));
    const py = (v: number) => H - P - ((v - yMin) / span) * (H - 2 * P);
    const dPath = res.equity.map((e, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(e.cum).toFixed(1)}`).join(' ');
    const zeroY = py(0);
    const last = ys[ys.length - 1];

    return (
      <div style={card}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
          누적 손익 <span style={{ fontWeight: 400, color: '#6b7280' }}>(일별 · {res.equity.length}일)</span>
        </div>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
          <line x1={P} y1={zeroY} x2={W - P} y2={zeroY} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3 3" />
          <path d={dPath} fill="none" stroke={last >= 0 ? '#dc2626' : '#2563eb'} strokeWidth="2" />
          <text x={P} y={14} fontSize="10" fill="#6b7280">{n0(yMax)}</text>
          <text x={P} y={H - 6} fontSize="10" fill="#6b7280">{n0(yMin)}</text>
          <text x={W - P} y={14} fontSize="10" fill={pnlColor(last)} textAnchor="end">
            최종 {n0(last)}
          </text>
        </svg>
      </div>
    );
  };

  return (
    <div style={{ padding: '16px', backgroundColor: '#f8fafc', minHeight: '100%' }}>
      {/* 조건 */}
      <div style={card}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af', marginBottom: '10px' }}>
          전략 백테스트 <span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280' }}>
            · 1분봉(kwbar1m) · 운영과 같은 전략 코드로 계산
          </span>
          <span style={{
            marginLeft: '10px', padding: '2px 8px', borderRadius: '10px',
            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
            fontSize: '11px', fontWeight: 600, color: '#1e40af',
          }}>
            계좌 {SIM_ACCOUNT.htsId} (가상체결) · NAV {n0(SIM_ACCOUNT.nav)}
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 0' }}>
          <span style={lbl}>전략</span>
          <select value={strategy} onChange={e => setStrategy(e.target.value)} style={inp}>
            <option value="I4">I4 Donchian (권장)</option>
            <option value="I1">I1 EMA 5/20 (구)</option>
          </select>

          {strategy === 'I4' && (
            <>
              <span style={lbl}>채널(분)</span>
              <select value={period} onChange={e => setPeriod(Number(e.target.value))} style={inp}>
                {[30, 45, 60, 90, 120, 180].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </>
          )}

          <span style={lbl}>기간</span>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} />

          <span style={lbl}>비중%</span>
          <input type="number" value={weiPct} min={1} max={100}
                 onChange={e => setWeiPct(Number(e.target.value))} style={{ ...inp, width: '52px' }} />

          <span style={lbl}>체결지연(분)</span>
          <select value={lagMin} onChange={e => setLagMin(Number(e.target.value))} style={inp}>
            {[0, 1, 2, 3].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
          <span style={lbl}>종목</span>
          <input value={prodIds} onChange={e => setProdIds(e.target.value)}
                 style={{ ...inp, width: '320px' }} placeholder="069500,114800,..." />
          <button
            onClick={run} disabled={loading}
            style={{
              padding: '5px 18px', backgroundColor: loading ? '#9ca3af' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '4px',
              fontSize: '12px', fontWeight: 500, cursor: loading ? 'default' : 'pointer',
            }}
          >{loading ? '실행 중…' : '백테스트 실행'}</button>
        </div>

        <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
          계좌는 <b>{SIM_ACCOUNT.htsId}(가상체결)</b> 로 고정된다 — 과거 데이터 모의실험이라
          실전·모의 계좌의 실제 잔고와 섞이면 안 된다.<br />
          체결가는 신호 봉 종가 + 지연(분) 뒤 종가다. 실측 주문→체결 지연이 45~56초라 <b>1분</b>을 권한다.
          호가 스프레드·시장충격은 반영되지 않는다.
        </div>
      </div>

      {/* 지난 실행 이력 — 재계산 없이 과거 결과를 다시 연다 */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>
            지난 실행
            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
              {runs.length}건 · 행을 누르면 재계산 없이 그 결과를 연다
            </span>
          </div>
          <button
            onClick={loadRuns}
            style={{
              padding: '3px 10px', backgroundColor: 'white', color: '#1e40af',
              border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '11px', cursor: 'pointer',
            }}
          >새로고침</button>
        </div>

        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>실행시각</th>
                <th style={{ ...th, textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>전략</th>
                <th style={{ ...th, textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>기간</th>
                <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>거래</th>
                <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>순손익</th>
                <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>승률</th>
                <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>PF</th>
                <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}></th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>
                  실행 이력이 없습니다 — 아래에서 실행하면 여기에 쌓입니다
                </td></tr>
              )}
              {runs.map(r => (
                <tr
                  key={r.runId}
                  onClick={() => openRun(r.runId)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: openRunId === r.runId ? '#eff6ff' : undefined,
                  }}
                >
                  <td style={{ ...td, textAlign: 'left' }}>{r.runAt}</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                    {r.strategy}{r.strategy === 'I4' ? `(${r.period})` : ''}
                  </td>
                  <td style={{ ...td, textAlign: 'left', color: '#6b7280' }}>
                    {r.startDate}~{r.endDate}
                  </td>
                  <td style={td}>{r.trades}</td>
                  <td style={{ ...td, color: pnlColor(r.pnl), fontWeight: 600 }}>{n0(r.pnl)}</td>
                  <td style={td}>{r.winRate.toFixed(1)}%</td>
                  <td style={{ ...td, color: r.pf >= 1 ? '#dc2626' : '#2563eb' }}>{r.pf.toFixed(2)}</td>
                  <td style={{ ...td, width: '40px' }}>
                    <span
                      onClick={ev => { ev.stopPropagation(); deleteRun(r.runId); }}
                      title="이력 삭제"
                      style={{ color: '#9ca3af', cursor: 'pointer', fontSize: '13px' }}
                    >✕</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {err && (
        <div style={{ ...card, borderColor: '#dc2626', color: '#dc2626', fontSize: '12px' }}>
          실행 실패: {err}
        </div>
      )}

      {res && !loading && (
        <>
          {/* 종합 */}
          <div style={card}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '10px' }}>
              종합
              {res.runAt && (
                <span style={{
                  marginLeft: '8px', padding: '1px 8px', borderRadius: '10px',
                  backgroundColor: '#fef3c7', fontSize: '11px', fontWeight: 600, color: '#92400e',
                }}>
                  지난 결과 · {res.runAt}
                </span>
              )}
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                {res.strategy}{res.strategy === 'I4' ? `(${res.period}분)` : ''} ·
                {' '}{res.startDate} ~ {res.endDate} · 비중 {res.weiPct}% · 지연 {res.lagMin}분
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
              {[
                ['거래', `${res.total.trades}건`, '#111827'],
                ['순손익', `${n0(res.total.pnl)}원`, pnlColor(res.total.pnl)],
                ['승률', `${res.total.winRate.toFixed(1)}%`, '#111827'],
                ['평균', `${n0(res.total.avgPnl)}원`, pnlColor(res.total.avgPnl)],
                ['PF', res.total.pf.toFixed(2), res.total.pf >= 1 ? '#dc2626' : '#2563eb'],
                ['MDD', `${n0(res.total.mdd)}원`, '#2563eb'],
              ].map(([k, v, c]) => (
                <div key={k as string}>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{k}</div>
                  <div style={{ fontSize: '17px', fontWeight: 700, color: c as string }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
              PF(Profit Factor) = 총이익 ÷ 총손실. <b>1.0 미만이면 구조적 손실</b>이다.
            </div>
          </div>

          {chart()}

          {/* 종목별 */}
          <div style={card}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
              종목별
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>종목</th>
                  <th style={th}>거래</th><th style={th}>순손익</th><th style={th}>승률</th>
                  <th style={th}>평균</th><th style={th}>MDD</th><th style={th}>PF</th>
                </tr>
              </thead>
              <tbody>
                {res.bySymbol.map(s => (
                  <tr key={s.prodId}>
                    <td style={{ ...td, textAlign: 'left' }}>
                      <b>{s.prodId}</b> <span style={{ color: '#6b7280' }}>{s.prodNm}</span>
                      {s.note && <span style={{ color: '#dc2626', marginLeft: '6px' }}>({s.note})</span>}
                    </td>
                    <td style={td}>{s.trades}</td>
                    <td style={{ ...td, color: pnlColor(s.pnl), fontWeight: 600 }}>{n0(s.pnl)}</td>
                    <td style={td}>{s.winRate.toFixed(1)}%</td>
                    <td style={{ ...td, color: pnlColor(s.avgPnl) }}>{n0(s.avgPnl)}</td>
                    <td style={{ ...td, color: '#2563eb' }}>{n0(s.mdd)}</td>
                    <td style={{ ...td, color: s.pf >= 1 ? '#dc2626' : '#2563eb', fontWeight: 600 }}>
                      {s.pf.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 종목별 누적손익 차트 — 4종목이면 4개가 각각 나온다 */}
          {!!res.symbolEquity?.length && (
            <div style={card}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '10px' }}>
                종목별 누적손익
                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                  {res.symbolEquity.length}종목 · 각각 따로 본다
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                gap: '12px',
              }}>
                {res.symbolEquity.map(se => {
                  const last = se.rows.length ? se.rows[se.rows.length - 1].cum : 0;
                  const tr = se.rows.reduce((a, r) => a + r.trades, 0);
                  const wn = se.rows.reduce((a, r) => a + r.wins, 0);
                  return (
                    <div key={se.prodId} style={{
                      border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px',
                      backgroundColor: '#fbfdff',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>
                          {se.prodNm || se.prodId}
                          <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: '5px', fontSize: '11px' }}>
                            {se.prodId}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: pnlColor(last) }}>
                          {n0(last)}원
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                        {tr}건 · 승률 {tr > 0 ? ((wn / tr) * 100).toFixed(0) : 0}% · {se.rows.length}일
                      </div>
                      {se.rows.length
                        ? sparkline(se.rows, 380, 120)
                        : <div style={{ padding: '28px', textAlign: 'center', color: '#9ca3af', fontSize: '11px' }}>
                            거래 없음
                          </div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 일별 거래·손익 — 그날 무슨 일이 있었는지 행 단위로 본다 */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>
                일별 거래·손익
                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                  거래가 있었던 날만 · {res.equity.length}일
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                수익 {res.equity.filter(e => e.pnl > 0).length}일 ·
                손실 {res.equity.filter(e => e.pnl < 0).length}일
              </div>
            </div>

            <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>일자</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>거래</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>승</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>패</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>승률</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>일손익</th>
                    <th style={{ ...th, position: 'sticky', top: 0, backgroundColor: 'white' }}>누적손익</th>
                  </tr>
                </thead>
                <tbody>
                  {res.equity.length === 0 && (
                    <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>
                      거래가 없습니다
                    </td></tr>
                  )}
                  {res.equity.map(e => (
                    <tr key={e.d}>
                      <td style={{ ...td, textAlign: 'left' }}>{e.d}</td>
                      <td style={td}>{e.trades}</td>
                      <td style={{ ...td, color: '#dc2626' }}>{e.wins}</td>
                      <td style={{ ...td, color: '#2563eb' }}>{e.loses}</td>
                      <td style={td}>{e.winRate.toFixed(0)}%</td>
                      <td style={{ ...td, color: pnlColor(e.pnl), fontWeight: 600 }}>{n0(e.pnl)}</td>
                      <td style={{ ...td, color: pnlColor(e.cum) }}>{n0(e.cum)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
              과거 날짜가 위다(시간 순). 누적손익은 기간 시작부터 그날까지의 합이다.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuantBacktest;
